import { createStore } from 'vuex'
import { supabase } from '../utils/supabaseClient'

export default createStore({
  state: {
    user: null,
    isLoading: false,
    toastMessage: null,
    toastType: 'info',
    // Quote themes and customization
    quoteThemes: [
      {
        id: 'default',
        name: 'ברירת מחדל',
        primaryColor: '#4f46e5', // indigo-600
        secondaryColor: '#f3f4f6', // gray-100
        accentColor: '#10b981', // emerald-500
        textColor: '#1f2937', // gray-800
        fontFamily: 'system-ui, -apple-system, sans-serif',
        headerStyle: 'gradient', // gradient, solid, minimal
        borderRadius: 'rounded-lg',
      },
      {
        id: 'elegant',
        name: 'אלגנטי',
        primaryColor: '#1e293b', // slate-800
        secondaryColor: '#f8fafc', // slate-50
        accentColor: '#c2410c', // orange-700
        textColor: '#0f172a', // slate-900
        fontFamily: 'Georgia, serif',
        headerStyle: 'minimal',
        borderRadius: 'rounded-md',
      },
      {
        id: 'modern',
        name: 'מודרני',
        primaryColor: '#0f766e', // teal-700
        secondaryColor: '#f0fdfa', // teal-50
        accentColor: '#9333ea', // purple-600
        textColor: '#134e4a', // teal-900
        fontFamily: 'system-ui, -apple-system, sans-serif',
        headerStyle: 'solid',
        borderRadius: 'rounded-xl',
      },
      {
        id: 'professional',
        name: 'מקצועי',
        primaryColor: '#0369a1', // sky-700
        secondaryColor: '#f0f9ff', // sky-50
        accentColor: '#b45309', // amber-700
        textColor: '#0c4a6e', // sky-900
        fontFamily: 'Arial, sans-serif',
        headerStyle: 'solid',
        borderRadius: 'rounded',
      },
      {
        id: 'creative',
        name: 'יצירתי',
        primaryColor: '#be185d', // pink-700
        secondaryColor: '#fdf2f8', // pink-50
        accentColor: '#65a30d', // lime-600
        textColor: '#831843', // pink-900
        fontFamily: 'system-ui, -apple-system, sans-serif',
        headerStyle: 'gradient',
        borderRadius: 'rounded-2xl',
      }
    ],
    // Current theme settings for the user
    userQuoteSettings: {
      themeId: 'default',
      customColors: {
        primaryColor: '', // If empty, use theme default
        secondaryColor: '',
        accentColor: '',
        textColor: '', // If empty, use theme default
      },
      customFont: '', // If empty, use theme default
    }
  },
  getters: {
    isAuthenticated: (state) => !!state.user,
    currentUser: (state) => state.user,
    // Quote theme getters
    availableQuoteThemes: (state) => state.quoteThemes,
    currentQuoteTheme: (state) => {
      const themeId = state.userQuoteSettings.themeId;
      return state.quoteThemes.find(theme => theme.id === themeId) || state.quoteThemes[0];
    },
    quoteCustomColors: (state) => state.userQuoteSettings.customColors,
    // Combined theme with custom overrides
    effectiveQuoteTheme: (state, getters) => {
      const baseTheme = getters.currentQuoteTheme;
      const customColors = state.userQuoteSettings.customColors;
      
      return {
        ...baseTheme,
        primaryColor: customColors.primaryColor || baseTheme.primaryColor,
        secondaryColor: customColors.secondaryColor || baseTheme.secondaryColor,
        accentColor: customColors.accentColor || baseTheme.accentColor,
        textColor: customColors.textColor || baseTheme.textColor,
        fontFamily: state.userQuoteSettings.customFont || baseTheme.fontFamily,
      };
    }
  },
  mutations: {
    SET_USER(state, user) {
      state.user = user
    },
    SET_LOADING(state, status) {
      state.isLoading = status
    },
    SET_TOAST(state, { message, type = 'info' }) {
      state.toastMessage = message
      state.toastType = type
    },
    CLEAR_TOAST(state) {
      state.toastMessage = null
    },
    // Quote theme mutations
    SET_QUOTE_THEME(state, themeId) {
      state.userQuoteSettings.themeId = themeId;
    },
    SET_CUSTOM_COLORS(state, customColors) {
      state.userQuoteSettings.customColors = {
        ...state.userQuoteSettings.customColors,
        ...customColors
      };
    },
    SET_CUSTOM_FONT(state, fontFamily) {
      state.userQuoteSettings.customFont = fontFamily;
    },
    RESET_THEME_CUSTOMIZATION(state) {
      state.userQuoteSettings.customColors = {
        primaryColor: '',
        secondaryColor: '',
        accentColor: '',
        textColor: '',
      };
      state.userQuoteSettings.customFont = '';
    }
  },
  actions: {
    async signUp({ commit }, { email, password, captchaToken }) {
      commit('SET_LOADING', true)
      try {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: captchaToken ? { captchaToken } : undefined
        })
        if (error) throw error
        commit('SET_USER', data.user)
        commit('SET_TOAST', { message: 'נרשמת בהצלחה! אנא אשר את האימייל שלך', type: 'success' })
        return data
      } catch (error) {
        // Translate common Supabase error messages to Hebrew
        let hebrewMessage = error.message;
        if (error.message === 'Invalid login credentials') {
          hebrewMessage = 'פרטי התחברות שגויים';
        } else if (error.message === 'User already registered') {
          hebrewMessage = 'משתמש כבר רשום במערכת';
        } else if (error.message === 'Password should be at least 6 characters') {
          hebrewMessage = 'הסיסמה צריכה להיות לפחות 6 תווים';
        } else if (error.message === 'Email not confirmed') {
          hebrewMessage = 'האימייל לא אומת';
        } else if (error.message === 'Captcha verification failed') {
          hebrewMessage = 'אימות האבטחה נכשל. אנא נסה שוב.';
        }
        commit('SET_TOAST', { message: hebrewMessage, type: 'error' })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async resetPassword({ commit }, { email }) {
      commit('SET_LOADING', true)
      try {
        // Send password reset request for the email
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/login`
        })
        
        // Only show detailed error for rate limiting
        if (error && error.message.includes('rate limit')) {
          commit('SET_TOAST', { 
            message: 'נסה שוב מאוחר יותר, חרגת ממספר הניסיונות המותר',
            type: 'error'
          })
          return
        }
        
        // Always show the same generic message regardless of whether the email exists 
        // or if there was another type of error
        commit('SET_TOAST', { 
          message: 'אם האימייל רשום במערכת, הוראות לאיפוס סיסמה יישלחו בקרוב', 
          type: 'info' 
        })
      } catch (error) {
        console.error('Reset password error:', error)
        // Don't show detailed errors to user - use generic message
        commit('SET_TOAST', { 
          message: 'אם האימייל רשום במערכת, הוראות לאיפוס סיסמה יישלחו בקרוב', 
          type: 'info' 
        })
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updatePassword({ commit }, { password }) {
      commit('SET_LOADING', true)
      try {
        const { error } = await supabase.auth.updateUser({
          password
        })
        
        if (error) throw error
        
        commit('SET_TOAST', { 
          message: 'הסיסמה עודכנה בהצלחה', 
          type: 'success' 
        })
        return true
      } catch (error) {
        console.error('Update password error:', error)
        commit('SET_TOAST', { 
          message: 'אירעה שגיאה בעדכון הסיסמה', 
          type: 'error' 
        })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async signIn({ commit }, { email, password, captchaToken }) {
      commit('SET_LOADING', true)
      try {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
          options: captchaToken ? { captchaToken } : undefined
        })
        if (error) throw error
        commit('SET_USER', data.user)
        commit('SET_TOAST', { message: 'התחברת בהצלחה!', type: 'success' })
        return data
      } catch (error) {
        // Translate common Supabase error messages to Hebrew
        let hebrewMessage = error.message;
        if (error.message === 'Invalid login credentials') {
          hebrewMessage = 'פרטי התחברות שגויים';
        } else if (error.message === 'Email not confirmed') {
          hebrewMessage = 'האימייל לא אומת';
        } else if (error.message === 'Captcha verification failed') {
          hebrewMessage = 'אימות האבטחה נכשל. אנא נסה שוב.';
        }
        commit('SET_TOAST', { message: hebrewMessage, type: 'error' })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async signInWithGoogle({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: window.location.origin + '/quotes',
          }
        })
        if (error) throw error
        // No need to commit USER here as the OAuth flow will redirect the user
        // They will be logged in automatically when they return to the app
        return data
      } catch (error) {
        // Translate common Supabase error messages to Hebrew
        let hebrewMessage = error.message;
        if (error.message === 'Invalid login credentials') {
          hebrewMessage = 'פרטי התחברות שגויים';
        } else if (error.message === 'Email not confirmed') {
          hebrewMessage = 'האימייל לא אומת';
        }
        commit('SET_TOAST', { message: hebrewMessage, type: 'error' })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async signOut({ commit }) {
      commit('SET_LOADING', true)
      try {
        const { error } = await supabase.auth.signOut()
        if (error) throw error
        commit('SET_USER', null)
        commit('SET_TOAST', { message: 'התנתקת בהצלחה', type: 'success' })
      } catch (error) {
        // Translate common Supabase error messages to Hebrew
        let hebrewMessage = error.message;
        if (error.message === 'Invalid login credentials') {
          hebrewMessage = 'פרטי התחברות שגויים';
        } else if (error.message === 'Email not confirmed') {
          hebrewMessage = 'האימייל לא אומת';
        }
        commit('SET_TOAST', { message: hebrewMessage, type: 'error' })
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchUser({ commit, dispatch }) {
      commit('SET_LOADING', true)
      try {
        const { data } = await supabase.auth.getUser()
        commit('SET_USER', data?.user || null)
        
        // Load user theme settings if user is authenticated
        if (data?.user) {
          dispatch('loadUserQuoteSettings')
        }
        
        return data?.user
      } catch (error) {
        console.error('Error fetching user:', error)
        commit('SET_USER', null)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    // Theme-related actions
    async loadUserQuoteSettings({ commit, state }) {
      try {
        if (!state.user) return;
        
        // Check if we're on a public quote page - if so, don't load user settings
        const currentPath = window.location.pathname;
        if (currentPath.includes('/quote/public/')) {
          console.log('On public quote page - not loading user theme settings');
          return;
        }
        
        const { data, error } = await supabase
          .from('user_preferences')
          .select('quote_settings')
          .eq('user_id', state.user.id)
          .single();
          
        if (error) throw error;
        
        if (data && data.quote_settings) {
          // Update local state with retrieved settings
          if (data.quote_settings.themeId) {
            commit('SET_QUOTE_THEME', data.quote_settings.themeId);
          }
          
          if (data.quote_settings.customColors) {
            commit('SET_CUSTOM_COLORS', data.quote_settings.customColors);
          }
          
          if (data.quote_settings.customFont) {
            commit('SET_CUSTOM_FONT', data.quote_settings.customFont);
          }
        }
      } catch (error) {
        console.error('Error loading user theme settings:', error);
        // If the user has no settings yet, we'll use the defaults
      }
    },
    
    async saveUserQuoteSettings({ state }) {
      try {
        if (!state.user) return;
        
        // Check if user preferences record exists first
        const { data: existingRecord } = await supabase
          .from('user_preferences')
          .select('id')
          .eq('user_id', state.user.id)
          .maybeSingle();
          
        if (existingRecord) {
          // Update existing record
          const { error } = await supabase
            .from('user_preferences')
            .update({
              quote_settings: state.userQuoteSettings,
              updated_at: new Date()
            })
            .eq('user_id', state.user.id);
            
          if (error) throw error;
        } else {
          // Insert new record
          const { error } = await supabase
            .from('user_preferences')
            .insert({
              user_id: state.user.id,
              quote_settings: state.userQuoteSettings,
              updated_at: new Date()
            });
            
          if (error) throw error;
        }
      } catch (error) {
        console.error('Error saving user theme settings:', error);
      }
    },
    
    async setQuoteTheme({ commit, dispatch }, themeId) {
      commit('SET_QUOTE_THEME', themeId);
      dispatch('saveUserQuoteSettings');
    },
    
    async setCustomColors({ commit, dispatch }, customColors) {
      commit('SET_CUSTOM_COLORS', customColors);
      dispatch('saveUserQuoteSettings');
    },
    
    async setCustomFont({ commit, dispatch }, fontFamily) {
      commit('SET_CUSTOM_FONT', fontFamily);
      dispatch('saveUserQuoteSettings');
    },
    
    async resetThemeCustomization({ commit, dispatch }) {
      commit('RESET_THEME_CUSTOMIZATION');
      dispatch('saveUserQuoteSettings');
    },

    // Captcha verification
    async verifyCaptcha(context, token) {
      try {
        console.log('Verifying captcha token:', token)
        console.log('Token type:', typeof token)
        
        const { data, error } = await supabase.functions.invoke('verify-captcha', {
          body: { token }
        })

        console.log('Supabase function response:', { data, error })

        if (error) {
          console.error('Supabase function error:', error)
          throw error
        }

        if (!data || !data.success) {
          console.error('Captcha verification failed from API:', data)
          throw new Error('Captcha verification failed')
        }

        console.log('Captcha verification successful!')
        return data
      } catch (error) {
        console.error('Captcha verification error:', error)
        throw new Error('Captcha verification failed')
      }
    }
  },
  modules: {}
})